import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  GetIntellipayWidget,
  PostPayment,
  purchaseCombineBundle,
} from "../services/PaymentService";
import { useAxiosInstance } from "../services/Interceptor";
import { RegisterAthlete } from "../services/EventsService";
import { useSelector } from "react-redux";
import { registerCoach } from "../services/CoachService";
import {
  registerAthlete,
  registerAthleteWithEventId,
} from "../services/AthleteService";
import {
  GetAdminPrice,
  GetEventCombinePrice,
  SaveAppliedCouponCode,
} from "../services/EventsService";
import { useTrackEvent } from "../hooks/useTrackEvent";
import {
  bundleSuccessMessage,
  chargedButIncompleteMessage,
  markBundleChargedButIncomplete,
} from "../utils/bundlePurchase";

// Fallback poll for widget readiness if the <script> load event is missed.
const WIDGET_READY_POLL_INTERVAL_MS = 500;
const WIDGET_READY_MAX_POLL_ATTEMPTS = 20; // 10-second ceiling

// Tight recheck right after `load` fires, in case the widget's own setup
// finishes a beat later — cheaper than falling back to the slower poll.
const WIDGET_LOAD_RECHECK_INTERVAL_MS = 20;
const WIDGET_LOAD_MAX_RECHECKS = 15; // ~300ms

// Re-initializing an already-loaded widget: no signal for when its own
// internal setup settles, so this stays a bounded wait (unverified vendor
// behavior — see PR discussion on TRAC-2846).
const WIDGET_REINIT_FIELD_SYNC_DELAY_MS = 100;

export default function PayWidget(props) {
  const { onIntellipayReady } = props;
  const apiInstance = useAxiosInstance();
  const accounts = useSelector((state) => state.account.register);
  const { subscriptionType, subscriptionPrice } = useSelector(
    (state) => state.account,
  );
  const navigate = useNavigate();

  const trackEvent = useTrackEvent();
  const trackEventRef = useRef(trackEvent);

  const subscriptionTypeRef = useRef(subscriptionType);
  const subscriptionPriceRef = useRef(subscriptionPrice);
  const intervalRef = useRef(null);
  const recheckTimeoutRef = useRef(null);
  const reinitSyncTimeoutRef = useRef(null);
  const isReInitializationRef = useRef(false);
  const isMountedRef = useRef(true);
  // Teardown for whichever waitForWidgetReady call is currently in flight.
  const activeWidgetWaitRef = useRef(null);

  useEffect(() => {
    trackEventRef.current = trackEvent;
  }, [trackEvent]);

  useEffect(() => {
    subscriptionTypeRef.current = subscriptionType;
    subscriptionPriceRef.current = subscriptionPrice;
  }, [subscriptionType, subscriptionPrice]);

  useEffect(() => {
    isMountedRef.current = true;

    // Track if this is a re-initialization (widget already exists)
    isReInitializationRef.current = !!(
      window.intellipay && window.__intellipayWidgetLoaded
    );

    getIntellipayWidget();

    // isMountedRef also guards the async continuation in getIntellipayWidget,
    // so a poll can't be started after unmount either, not just left running.
    // Don't reset the widget flag since the script persists across route changes
    return () => {
      isMountedRef.current = false;
      cancelPendingWidgetWork();
      // Don't reset window.__intellipayWidgetLoaded here
      // The script stays in the DOM during React Router navigation
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetIntellipayWidget = () => {
    // Only reset after non-approval, not on unmount
    window.__intellipayWidgetLoaded = false;
  };

  const handleFixAmount = (inputValue) => {
    if (typeof inputValue !== "string") {
      if (typeof inputValue === "number") {
        return inputValue.toFixed(2);
      } else {
        console.warn("Invalid amount format. Returning default value.");
        return "0.00";
      }
    }

    if (
      window.intellipay &&
      typeof window.intellipay.fixAmount === "function"
    ) {
      let tempamount = window.intellipay.fixAmount(inputValue);
      return tempamount;
    } else {
      console.warn("fixAmount is missing! Using fallback.");
      return parseFloat(inputValue.replace(/[^0-9.]/g, "") || "0").toFixed(2);
    }
  };
  const accountsRef = useRef(accounts);

  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  const getAthletePricing = async () => {
    const res = await GetAdminPrice(apiInstance);
    const athlete = res?.data?.find(
      (item) => item.role.toLowerCase() === "athlete",
    );
    return athlete?.pricingOptions || {};
  };

  const getCombinePositionDrillPrice = async (response, props) => {
    const combinePriceRes = await GetEventCombinePrice(
      apiInstance,
      props.eventId,
    );
    let combineDrillPayload = [];

    if (props.subscriptionType === "combinePosition") {
      combineDrillPayload.push({
        userId: props.userId,
        amount: handleFixAmount(combinePriceRes?.data?.combinePrice),
        isCombine: true,
        isPositionDrill: false,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });

      combineDrillPayload.push({
        userId: props.userId,
        amount: handleFixAmount(combinePriceRes?.data?.positionDrillPrice),
        isCombine: false,
        isPositionDrill: true,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });
    } else {
      combineDrillPayload.push({
        userId: props.userId,
        amount: handleFixAmount(combinePriceRes?.data?.combinePrice),
        isCombine: true,
        isPositionDrill: false,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });
    }

    return combineDrillPayload;
  };

  const postAppliedCouponCode = async (code) => {
    await SaveAppliedCouponCode(apiInstance, code);
    localStorage.removeItem("appliedCouponCode");
  };

  const postPaymentSuccess = async (response, props) => {
    const { bundleChoice } = props;

    if (bundleChoice) {
      // Returning athlete bundle purchase: register for the originally selected
      // combine, then post the bundle purchase once. Position drills are covered
      // by the bundle total and granted for every eligible combine by the
      // combine-bundle route, so no separate payments are sent.
      const transactionId = response?.receiptelements?.transid || "";

      // The card has already been charged by this point, so any failure must
      // leave the athlete and support a way to reconcile or refund it, and
      // must stop the bundle from being charged again.
      const reportChargedButIncomplete = (reason) => {
        toast.error(chargedButIncompleteMessage(transactionId), {
          position: "top-right",
          autoClose: false,
        });
        props.onChargedButIncomplete?.(transactionId);
        markBundleChargedButIncomplete(transactionId, bundleChoice.bundleId);
        trackEventRef.current("Registration4_5_PaymentFailed", {
          event_id: props.eventId,
          bundle_id: bundleChoice.bundleId,
          transaction_id: transactionId,
          reason,
        });
      };

      try {
        const regRes = await RegisterAthlete(
          apiInstance,
          props.eventId,
          props.userId,
          props.tShirtSize,
        );

        if (regRes.status !== 200) {
          reportChargedButIncomplete("registration_status_not_200");
          return;
        }

        const purchaseRes = await purchaseCombineBundle(apiInstance, {
          userId: props.userId,
          combineBundleId: bundleChoice.bundleId,
          eventId: props.eventId,
          amount: handleFixAmount(Number(bundleChoice.bundlePrice || 0)),
          transactionId,
          intellipayToken: String(response?.custid || ""),
          tShirtSize: props.tShirtSize,
        });

        if (purchaseRes.status === 200) {
          localStorage.removeItem("appliedCouponCode");
          localStorage.removeItem("appliedCouponType");
          localStorage.removeItem("appliedCouponDiscount");
          toast.success(bundleSuccessMessage(bundleChoice), {
            position: "top-right",
          });
          props.onRegistrationSuccess();
        } else {
          reportChargedButIncomplete("purchase_status_not_200");
        }
      } catch (error) {
        reportChargedButIncomplete(error?.message || "unknown");
      }
      return;
    }

    let combineDrillPayload = [];
    if (
      props.subscriptionType === "combine" ||
      props.subscriptionType === "combinePosition"
    ) {
      combineDrillPayload = await getCombinePositionDrillPrice(response, props);
    }

    const currentSubscriptionType = subscriptionTypeRef.current;

    const isCombine = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("combine")
      : currentSubscriptionType === "combine";

    const isMonthlySubscription = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("monthly")
      : currentSubscriptionType === "monthly";

    const isAnnualSubscription = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("annual")
      : currentSubscriptionType === "annual";

    const isCombinePositionDrill = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("positionDrill")
      : currentSubscriptionType === "positionDrill";

    const isSubscription = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("subscription")
      : currentSubscriptionType === "subscription";

    let combinePrice = 0;
    let positionDrillPrice = 0;
    let athletePrices = {};
    let paymentPayloads = [];

    const combinePriceResponse = await GetEventCombinePrice(
      apiInstance,
      props.eventId,
    );

    if (isCombine) {
      combinePrice = combinePriceResponse?.data?.combinePrice || 0;

      paymentPayloads.push({
        userId: props.userId,
        amount: handleFixAmount(combinePrice),
        isCombine: true,
        isPositionDrill: false,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });
    }

    positionDrillPrice = combinePriceResponse?.data?.positionDrillPrice || 0;
    athletePrices = await getAthletePricing();

    if (isCombinePositionDrill) {
      paymentPayloads.push({
        userId: props.userId,
        amount: handleFixAmount(positionDrillPrice),
        isCombine: false,
        isPositionDrill: true,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });
    }

    if (isSubscription) {
      paymentPayloads.push({
        userId: props.userId,
        amount: handleFixAmount(athletePrices.annualPrice ?? 0),
        isCombine: false,
        isPositionDrill: false,
        isAnnualSubscription: true,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });
    }

    if (isAnnualSubscription || isMonthlySubscription) {
      paymentPayloads.push({
        userId: props.userId,
        amount: handleFixAmount(subscriptionPriceRef.current),
        isCombine: false,
        isPositionDrill: false,
        isAnnualSubscription,
        isMonthlySubscription,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });
    }

    try {
      const currentAccounts = accountsRef.current;
      if (currentAccounts.length > 0) {
        const user = currentAccounts[0];
        if (user.registered) {
          for (const payload of paymentPayloads) {
            const storedData = JSON.parse(
              localStorage.getItem("registeredAccount") || "{}",
            );
            const userReg = user.length ? user[0] : storedData;
            const updatedPaymentPayload = {
              ...payload,
              userId: userReg.userId,
              eventId: 0,
            };
            const paid = await PostPayment(apiInstance, updatedPaymentPayload);
            if (paid.status === 200) {
              const couponCode = localStorage.getItem("appliedCouponCode");
              if (couponCode) {
                await postAppliedCouponCode(couponCode);
              }
              toast.success(
                "Athlete payment successful.Verify Your Email to Get Started!",
                {
                  position: "top-right",
                },
              );
              window.location.href = "/athlete/dashboard";
            } else {
              toast.error("Your Payment is failed.", { position: "top-right" });
            }
          }
        } else if (user.role === "university") {
          const reg = await registerCoach(apiInstance, user);
          if (reg.status === 200) {
            const newUserId = reg.data.id;
            for (const payload of paymentPayloads) {
              const updatedPaymentPayload = {
                ...payload,
                userId: newUserId,
                eventId: 0,
              };

              const paid = await PostPayment(
                apiInstance,
                updatedPaymentPayload,
              );
              if (paid.status === 200) {
                toast.success("Coach registration & payment successful!", {
                  position: "top-right",
                });
                const couponCode = localStorage.getItem("appliedCouponCode");
                if (couponCode) {
                  await postAppliedCouponCode(couponCode);
                }
                navigate("/register/registrationsuccess");
              } else {
                toast.error("Payment after coach registration failed.", {
                  position: "top-right",
                });
              }
            }
          } else {
            toast.error("Coach registration failed.", {
              position: "top-right",
            });
          }
        } else if (user.role === "athlete") {
          const reg = await registerAthlete(apiInstance, "0", user);
          if (reg.status === 200) {
            const newUserId = reg.data.id;
            for (const payload of paymentPayloads) {
              const updatedPaymentPayload = {
                ...payload,
                userId: newUserId,
                eventId: 0,
              };
              const paid = await PostPayment(
                apiInstance,
                updatedPaymentPayload,
              );
              if (paid.status === 200) {
                toast.success("Athlete registration & payment successful!", {
                  position: "top-right",
                });
                const couponCode = localStorage.getItem("appliedCouponCode");
                if (couponCode) {
                  await postAppliedCouponCode(couponCode);
                }
                navigate("/register/registrationsuccess");
              } else {
                toast.error("Payment after athlete registration failed.", {
                  position: "top-right",
                });
              }
            }
          } else {
            toast.error("Athlete registration failed.", {
              position: "top-right",
            });
          }
        } else if (user.role === "spotcombine") {
          const spotcombineRegRes = await registerAthleteWithEventId(
            apiInstance,
            user,
            props.eventId,
          );
          if (spotcombineRegRes.status === 200) {
            const newUserId = spotcombineRegRes.data.id;
            let allSuccessful = true;

            for (const payload of paymentPayloads) {
              const updatedPaymentPayload = {
                ...payload,
                userId: newUserId,
                eventId: props.eventId,
              };

              const paid = await PostPayment(
                apiInstance,
                updatedPaymentPayload,
              );

              if (paid.status !== 200) {
                allSuccessful = false;
                toast.error("Payment after spot registration failed.", {
                  position: "top-right",
                });
                break;
              }
            }

            if (allSuccessful) {
              const couponCode = localStorage.getItem("appliedCouponCode");
              if (couponCode) {
                await postAppliedCouponCode(couponCode);
              }
              trackEventRef.current("Registration5_RegistrationCompleted", {
                event_id: props.eventId,
                role: "spotcombine",
              });
              toast.success("Athlete spot registration & payment successful!", {
                position: "top-right",
              });
              navigate("/register/registrationsuccess");
            }
          } else {
            toast.error("Athlete spot registration failed.", {
              position: "top-right",
            });
          }
        } else {
          const regRes = await RegisterAthlete(
            apiInstance,
            user.eventId,
            user.userId,
            props.tShirtSize,
          );
          if (regRes.status === 200) {
            for (const payload of paymentPayloads) {
              const paidRes = await PostPayment(apiInstance, payload);
              if (paidRes.status === 200) {
                const couponCode = localStorage.getItem("appliedCouponCode");
                if (couponCode) {
                  await postAppliedCouponCode(couponCode);
                }
                toast.success(
                  "Athlete event registration and payment is successful",
                  {
                    position: "top-right",
                  },
                );
                props.onRegistrationSuccess();
              } else {
                toast.error("Payment after event registration failed.", {
                  position: "top-right",
                });
              }
            }
          } else {
            toast.error("Your event registration failed", {
              position: "top-right",
            });
          }
        }
      } else {
        const regRes = await RegisterAthlete(
          apiInstance,
          props.eventId,
          props.userId,
          props.tShirtSize,
        );
        if (regRes.status === 200) {
          let allSuccessful = true;

          for (const combineDrills of combineDrillPayload) {
            const paidRes = await PostPayment(apiInstance, combineDrills);
            if (paidRes.status !== 200) {
              allSuccessful = false;
              toast.error("Payment after event registration failed.", {
                position: "top-right",
              });
              break;
            }
          }

          if (allSuccessful) {
            const couponCode = localStorage.getItem("appliedCouponCode");
            if (couponCode) {
              await postAppliedCouponCode(couponCode);
            }
            toast.success(
              "Athlete event combine registration and payment is successful",
              {
                position: "top-right",
              },
            );
            props.onRegistrationSuccess();
          }
        } else {
          toast.error("Your event registration failed", {
            position: "top-right",
          });
        }
      }
    } catch (error) {
      trackEventRef.current("Registration4_5_PaymentFailed", {
        event_id: props.eventId,
        reason: error.message ?? "unknown",
      });
      toast.error("Payment processing failed - " + error.error, {
        position: "top-right",
      });
    }
  };

  // Shared by all three ready paths below.
  const setupWidgetCallbacks = () => {
    if (typeof window.intellipay.disable === "function") {
      window.intellipay.disable("account");
    }
    window.intellipay.runOnApproval((response) => {
      postPaymentSuccess(response, props);
    });
    window.intellipay.runOnNonApproval((res) => {
      console.error("Transaction not approved!", res);
      resetIntellipayWidget();
    });
    window.intellipay.runOnClose(() => {
      // Don't trigger onClose callback when user simply closes the widget
      // The callback should only be triggered after successful payment
    });
  };

  const markWidgetReady = (trackOpened) => {
    window.__intellipayWidgetLoaded = true;
    if (trackOpened) {
      trackEventRef.current("Registration4_OpenedPaymentModal", {
        event_id: props.eventId,
      });
    }
    onIntellipayReady?.(true);
  };

  // `scriptEl` may be null if the script was already in the DOM — the poll
  // below is the only path in that case.
  const waitForWidgetReady = (scriptEl, onReady) => {
    // Self-supersede too (on top of getIntellipayWidget's own
    // cancelPendingWidgetWork() call) — idempotent since stopWaiting leaves
    // everything null/settled, so this stays safe even if a future call
    // site invokes it without cancelling first.
    activeWidgetWaitRef.current?.();

    let settled = false;

    const stopWaiting = () => {
      settled = true;
      if (scriptEl) scriptEl.removeEventListener("load", handleScriptLoad);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (recheckTimeoutRef.current) {
        clearTimeout(recheckTimeoutRef.current);
        recheckTimeoutRef.current = null;
      }
      if (activeWidgetWaitRef.current === stopWaiting) {
        activeWidgetWaitRef.current = null;
      }
    };
    activeWidgetWaitRef.current = stopWaiting;

    const finish = () => {
      if (settled || !isMountedRef.current) {
        stopWaiting();
        return;
      }
      stopWaiting();
      onReady();
    };

    const handleScriptLoad = () => {
      if (window.intellipay) {
        finish();
        return;
      }
      // `load` fired but the widget hasn't set itself up yet — recheck fast
      // for a bit instead of waiting for the next slow poll tick.
      let rechecks = 0;
      const recheck = () => {
        if (settled || !isMountedRef.current) return;
        if (window.intellipay) {
          finish();
          return;
        }
        rechecks++;
        if (rechecks < WIDGET_LOAD_MAX_RECHECKS) {
          recheckTimeoutRef.current = setTimeout(
            recheck,
            WIDGET_LOAD_RECHECK_INTERVAL_MS,
          );
        }
        // Otherwise leave it to the poll below, still running.
      };
      recheckTimeoutRef.current = setTimeout(
        recheck,
        WIDGET_LOAD_RECHECK_INTERVAL_MS,
      );
    };

    if (scriptEl) {
      scriptEl.addEventListener("load", handleScriptLoad);
    }

    let attempts = 0;
    intervalRef.current = setInterval(() => {
      if (!isMountedRef.current) {
        stopWaiting();
        return;
      }
      attempts++;
      if (window.intellipay) {
        finish();
      } else if (attempts >= WIDGET_READY_MAX_POLL_ATTEMPTS) {
        stopWaiting();
        console.error(
          "Intellipay widget failed to load after maximum attempts",
        );
        // Tell the caller it failed too, or it'll spin forever waiting.
        onIntellipayReady?.(false);
      }
    }, WIDGET_READY_POLL_INTERVAL_MS);
  };

  // Cancels whatever widget-loading work (a waitForWidgetReady wait, or a
  // branch-1 reinit field sync) is currently in flight — called both on
  // unmount and at the top of every getIntellipayWidget invocation. Not
  // load-bearing today (this effect has no deps, so it can't legitimately
  // re-run on the same instance without StrictMode, which this app doesn't
  // use), but cheap insurance if either of those ever changes.
  const cancelPendingWidgetWork = () => {
    activeWidgetWaitRef.current?.();
    if (reinitSyncTimeoutRef.current) {
      clearTimeout(reinitSyncTimeoutRef.current);
      reinitSyncTimeoutRef.current = null;
    }
  };

  const getIntellipayWidget = async () => {
    cancelPendingWidgetWork();

    // Check if window.intellipay exists (script loaded)
    if (window.intellipay) {
      // Re-register the callbacks first, then initialize
      setupWidgetCallbacks();

      try {
        window.intellipay.initialize();
        // No vendor-exposed signal for "initialize() finished its own
        // internal (re-)setup" — this bounded wait is a deliberate fallback
        // for that, not for React's DOM commit.
        const syncReinitFields = () => {
          reinitSyncTimeoutRef.current = null;
          if (!isMountedRef.current || !window.intellipay) return;
          try {
            if (typeof window.intellipay.updatefields === "function") {
              window.intellipay.updatefields();
            }
            if (typeof window.intellipay.readyHandler === "function") {
              window.intellipay.readyHandler();
            }
          } catch (e) {
            console.warn("Error syncing fields after re-initialize:", e);
          }
        };
        reinitSyncTimeoutRef.current = setTimeout(
          syncReinitFields,
          WIDGET_REINIT_FIELD_SYNC_DELAY_MS,
        );
      } catch (e) {
        console.warn("Error during initialize:", e);
      }

      markWidgetReady(false);
      return;
    }

    // Check if script already exists in DOM but not fully loaded yet
    const existingScript = document.querySelector("script[src*='intellipay']");
    const existingContainer =
      document.querySelector("#intellipay-container") ||
      document.querySelector("[id*='intellipay']");

    if (existingScript || existingContainer) {
      waitForWidgetReady(existingScript, () => {
        setupWidgetCallbacks();
        window.intellipay.initialize();
        markWidgetReady(true);
      });
      return;
    }

    // Load the widget for the first time
    const response = await GetIntellipayWidget(apiInstance);
    if (!isMountedRef.current) return;

    if (response.success) {
      const range = document.createRange();
      const fragment = range.createContextualFragment(response.data);

      // Grab the script reference before appending — once inserted its
      // nodes move out of `fragment`.
      let scriptEl = null;
      if (!document.querySelector("script[src*='intellipay']")) {
        scriptEl = fragment.querySelector("script[src*='intellipay']");
        document.documentElement.appendChild(fragment);
      }

      waitForWidgetReady(scriptEl, () => {
        setupWidgetCallbacks();
        window.intellipay.initialize();
        markWidgetReady(true);
      });
    }
  };

  useEffect(() => {
    // Manually handle submit button clicks ONLY when widget is re-initialized
    const handleSubmitClick = (e) => {
      if (e.target.dataset.ipayname === "submit") {
        // ONLY manually intervene if this was a re-initialization
        if (isReInitializationRef.current && window.intellipay) {
          e.preventDefault();
          e.stopPropagation();

          if (typeof window.intellipay.disable === "function") {
            window.intellipay.disable("account");
          }

          // Update fields right before opening to ensure current values are used
          if (typeof window.intellipay.updatefields === "function") {
            window.intellipay.updatefields();
          }

          // Set values explicitly using setvalue if available
          const amountField = document.querySelector(
            '.ipayfield[data-ipayname="amount"]',
          );
          const emailField = document.querySelector(
            '.ipayfield[data-ipayname="email"]',
          );
          const accountField = document.querySelector(
            '.ipayfield[data-ipayname="account"]',
          );

          if (typeof window.intellipay.setvalue === "function") {
            if (accountField) {
              window.intellipay.setvalue("account", accountField.value);
            }
            if (amountField) {
              window.intellipay.setvalue("amount", amountField.value);
            }
            if (emailField) {
              window.intellipay.setvalue("email", emailField.value);
            }
          }

          // Try to trigger the form submission properly
          // First try onSubmitInternal which should process the form values
          if (typeof window.intellipay.onSubmitInternal === "function") {
            window.intellipay.onSubmitInternal(e);
          } else if (typeof window.intellipay.onSubmit === "function") {
            window.intellipay.onSubmit(e);
          } else if (typeof window.intellipay.checkform === "function") {
            const formValid = window.intellipay.checkform();
            if (
              formValid &&
              typeof window.intellipay.openModal === "function"
            ) {
              window.intellipay.openModal();
            }
          } else if (typeof window.intellipay.openModal === "function") {
            window.intellipay.openModal();
          }
        }
      }
    };

    // Use capture phase to intercept before Intellipay's listener
    document.addEventListener("click", handleSubmitClick, true);
    return () => document.removeEventListener("click", handleSubmitClick, true);
  }, []);

  return (
    <div>
      <input
        className="ipayfield"
        data-ipayname="account"
        type="hidden"
        value={0}
      />
      <input
        className="ipayfield"
        data-ipayname="email"
        type="hidden"
        value={props.userEmail || ""}
      />
      <input
        className="ipayfield"
        data-ipayname="amount"
        type="hidden"
        value={props.paymentAmount || 0}
      />
    </div>
  );
}
